import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Save, Calendar, MapPin, Clock, Users, Shield, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusOptions = [
  { value: 'bozza', label: 'Bozza' },
  { value: 'programmato', label: 'Programmato' },
  { value: 'in_corso', label: 'In Corso' },
  { value: 'concluso', label: 'Concluso' },
  { value: 'annullato', label: 'Annullato' }
];

const tipologieEvento = [
  'Attività ordinaria',
  'Evento speciale',
  'Formazione',
  'Assemblea',
  'Raccolta fondi',
  'Servizio territoriale',
  'Altro'
];

export default function EventoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    titolo: '',
    tipologia: '',
    descrizione: '',
    data: '',
    orario_inizio: '',
    orario_fine: '',
    luogo: '',
    referente: '',
    volontari_previsti: '',
    note_organizzative: '',
    note_sicurezza: '',
    stato: 'bozza',
    copertura_richiesta: false,
    copertura_confermata: false
  });

  useEffect(() => {
    if (isEdit) {
      fetchEvento();
    }
  }, [id]);

  const fetchEvento = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/eventi/${id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setFormData({
          ...data,
          volontari_previsti: data.volontari_previsti?.toString() || ''
        });
      } else {
        toast.error('Evento non trovato');
        navigate('/eventi');
      }
    } catch (error) {
      toast.error('Errore nel caricamento');
      navigate('/eventi');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.titolo || !formData.data) {
      toast.error('Titolo e data sono obbligatori');
      return;
    }

    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
        volontari_previsti: formData.volontari_previsti ? parseInt(formData.volontari_previsti) : null
      };

      const url = isEdit 
        ? `${API_URL}/api/eventi/${id}`
        : `${API_URL}/api/eventi`;
      
      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        const timestamp = new Date().toLocaleString('it-IT');
        toast.success(
          isEdit 
            ? `Evento aggiornato correttamente il ${timestamp}`
            : `Evento creato correttamente il ${timestamp}`
        );
        navigate('/eventi');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore durante il salvataggio');
      }
    } catch (error) {
      toast.error('Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="evento-form">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/eventi')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Indietro
        </Button>
        <div className="page-header mb-0">
          <h1 className="page-title">{isEdit ? 'Modifica Evento' : 'Nuovo Evento'}</h1>
          <p className="page-subtitle">
            {isEdit ? 'Modifica i dati dell\'evento' : 'Inserisci i dati del nuovo evento'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6">
          {/* Informazioni Base */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Calendar className="w-5 h-5 text-[#0055BB]" />
                Informazioni Evento
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="titolo">Titolo Evento *</Label>
                <Input
                  id="titolo"
                  value={formData.titolo}
                  onChange={(e) => handleChange('titolo', e.target.value)}
                  placeholder="Es: Giornata raccolta alimentare"
                  required
                  data-testid="input-titolo"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tipologia">Tipologia</Label>
                <Select value={formData.tipologia} onValueChange={(v) => handleChange('tipologia', v)}>
                  <SelectTrigger data-testid="select-tipologia">
                    <SelectValue placeholder="Seleziona tipologia" />
                  </SelectTrigger>
                  <SelectContent>
                    {tipologieEvento.map(tipo => (
                      <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="stato">Stato</Label>
                <Select value={formData.stato} onValueChange={(v) => handleChange('stato', v)}>
                  <SelectTrigger data-testid="select-stato">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="descrizione">Descrizione</Label>
                <Textarea
                  id="descrizione"
                  value={formData.descrizione}
                  onChange={(e) => handleChange('descrizione', e.target.value)}
                  placeholder="Descrivi l'evento..."
                  rows={3}
                  data-testid="input-descrizione"
                />
              </div>
            </CardContent>
          </Card>

          {/* Data e Orario */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Clock className="w-5 h-5 text-[#0055BB]" />
                Data e Orario
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="data">Data *</Label>
                <Input
                  id="data"
                  type="date"
                  value={formData.data}
                  onChange={(e) => handleChange('data', e.target.value)}
                  required
                  data-testid="input-data"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="orario_inizio">Orario Inizio</Label>
                <Input
                  id="orario_inizio"
                  type="time"
                  value={formData.orario_inizio}
                  onChange={(e) => handleChange('orario_inizio', e.target.value)}
                  data-testid="input-orario-inizio"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="orario_fine">Orario Fine</Label>
                <Input
                  id="orario_fine"
                  type="time"
                  value={formData.orario_fine}
                  onChange={(e) => handleChange('orario_fine', e.target.value)}
                  data-testid="input-orario-fine"
                />
              </div>
            </CardContent>
          </Card>

          {/* Luogo e Organizzazione */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <MapPin className="w-5 h-5 text-[#0055BB]" />
                Luogo e Organizzazione
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="luogo">Luogo</Label>
                <Input
                  id="luogo"
                  value={formData.luogo}
                  onChange={(e) => handleChange('luogo', e.target.value)}
                  placeholder="Indirizzo o nome del luogo"
                  data-testid="input-luogo"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="referente">Referente</Label>
                <Input
                  id="referente"
                  value={formData.referente}
                  onChange={(e) => handleChange('referente', e.target.value)}
                  placeholder="Nome del responsabile"
                  data-testid="input-referente"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="volontari_previsti">Volontari Previsti</Label>
                <Input
                  id="volontari_previsti"
                  type="number"
                  min="0"
                  value={formData.volontari_previsti}
                  onChange={(e) => handleChange('volontari_previsti', e.target.value)}
                  placeholder="Numero"
                  data-testid="input-volontari"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="note_organizzative">Note Organizzative</Label>
                <Textarea
                  id="note_organizzative"
                  value={formData.note_organizzative}
                  onChange={(e) => handleChange('note_organizzative', e.target.value)}
                  placeholder="Dettagli organizzativi, materiali necessari, ecc."
                  rows={3}
                  data-testid="input-note-org"
                />
              </div>
            </CardContent>
          </Card>

          {/* Sicurezza e Copertura */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Shield className="w-5 h-5 text-[#0055BB]" />
                Sicurezza e Copertura Assicurativa
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="note_sicurezza">Note Sicurezza / Rischio Specifico</Label>
                <Textarea
                  id="note_sicurezza"
                  value={formData.note_sicurezza}
                  onChange={(e) => handleChange('note_sicurezza', e.target.value)}
                  placeholder="Eventuali rischi specifici, DPI necessari, procedure di sicurezza"
                  rows={2}
                  data-testid="input-note-sicurezza"
                />
              </div>
              <div className="flex flex-col md:flex-row md:items-center gap-6 pt-2">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="copertura_richiesta"
                    checked={formData.copertura_richiesta}
                    onCheckedChange={(checked) => handleChange('copertura_richiesta', checked)}
                    data-testid="checkbox-copertura-richiesta"
                  />
                  <Label htmlFor="copertura_richiesta" className="cursor-pointer">
                    Copertura assicurativa richiesta
                  </Label>
                </div>
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="copertura_confermata"
                    checked={formData.copertura_confermata}
                    onCheckedChange={(checked) => handleChange('copertura_confermata', checked)}
                    data-testid="checkbox-copertura-confermata"
                  />
                  <Label htmlFor="copertura_confermata" className="cursor-pointer">
                    Copertura confermata
                  </Label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/eventi')}>
              Annulla
            </Button>
            <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-btn">
              {saving ? <span className="spinner mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {isEdit ? 'Salva Modifiche' : 'Crea Evento'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
